import identity from "lodash/identity";

import { NO_DATA_SIGN } from "app/filters/const";
import {
    timeFilter,
    swNumberFilter,
    percentageFilter,
    suffixFilter,
    percentageSignFilter,
    abbrNumberWithThresholdFilter,
} from "app/filters/ngFilters";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";

export type FormatFunction = (value: string | number) => string;

const FORMAT_DECIMAL_PLACE = 2;

export enum FormatterType {
    Visits = "visits",
    PageViews = "pageviews",
    Time = "time",
    PagesPerVisit = "pagesPerVisit",
    Percentage = "percentage",
    PercentageShare = "percentageShare",
}

const FORMATTER_FUNCTIONS: Record<FormatterType, FormatFunction> = {
    [FormatterType.Visits]: (v) => abbrNumberWithThresholdFilter()({ min: MIN_RELIABLE_METRIC_VALUE }, v) as string,
    [FormatterType.PageViews]: (v) => abbrNumberWithThresholdFilter()({ min: MIN_RELIABLE_METRIC_VALUE }, v) as string,
    // @ts-expect-error FIXME: Type 'string' is not assignable to type 'number | "NA" | null | undefined'
    [FormatterType.Time]: (v) => timeFilter()(v, null),
    [FormatterType.PagesPerVisit]: (v) => swNumberFilter()(v || 0, FORMAT_DECIMAL_PLACE),
    [FormatterType.Percentage]: (v) =>
        suffixFilter()(percentageFilter()(v, FORMAT_DECIMAL_PLACE, NO_DATA_SIGN), "%") as string,
    [FormatterType.PercentageShare]: (v) => percentageSignFilter()(v, FORMAT_DECIMAL_PLACE, NO_DATA_SIGN),
};

const NAMING_CONVENTIONS: ReadonlyArray<{
    pattern: RegExp;
    formatterType: FormatterType;
}> = [
    {
        // visits, desktop_visits, mobileweb_visitors
        pattern: /^(desktop_|mobileweb_)?(visits|visitors)$/,
        formatterType: FormatterType.Visits,
    },
    {
        // direct_visits, organic_search_visits, search_visits, etc.
        pattern:
            /^(direct|mail|referrals|social|organic_social|paid_social|organic_search|paid_search|search|displayads|gen_ai|affiliate)_visits$/,
        formatterType: FormatterType.Visits,
    },
    {
        pattern: /^avg_monthly_estimated_visits$/,
        formatterType: FormatterType.Visits,
    },
    {
        // pageviews, desktop_pageviews
        pattern: /^(desktop_|mobileweb_)?pageviews$/,
        formatterType: FormatterType.PageViews,
    },
    {
        // avg_visit_duration, desktop_avg_visit_duration
        pattern: /^(desktop_|mobileweb_)?avg_visit_duration$/,
        formatterType: FormatterType.Time,
    },
    {
        // pages_per_visit, mobileweb_pages_per_visit
        pattern: /^(desktop_|mobileweb_)?pages_per_visit$/,
        formatterType: FormatterType.PagesPerVisit,
    },
    {
        // bounce_rate, desktop_bounce_rate
        pattern: /^(desktop_|mobileweb_)?bounce_rate$/,
        formatterType: FormatterType.Percentage,
    },
    {
        // conversion_rate
        pattern: /^conversion_rate$/,
        formatterType: FormatterType.Percentage,
    },
    {
        // direct_visits_share, organic_search_visits_share
        pattern: /_share$/,
        formatterType: FormatterType.PercentageShare,
    },
];

export const resolveColumnValueFormatter = <R extends LeadsSearchRow>(dataIndex: keyof R): FormatFunction => {
    const { formatterType } = NAMING_CONVENTIONS.find(({ pattern }) => pattern.test(dataIndex as string)) ?? {};

    if (formatterType) {
        return FORMATTER_FUNCTIONS[formatterType];
    }

    return identity;
};
